import {
  isBoolean,
  isFunction,
  isList,
  isNumber,
  isObjectLike,
  isString,
} from './guards';
import type { JsonObject, JsonValue } from './types';

/** True for a value made only of strings, finite numbers, booleans, null, arrays and plain objects of those. */
export function isJsonValue<T>(value: T): value is T & JsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean')
    return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  return Object.values(value).every(isJsonValue);
}

/** What a value that is not JSON holds that JSON cannot: where it is and what it is; null for JSON. */
export function jsonProblem<T>(value: T, at = ''): string | null {
  const where = at || 'the value';
  if (value === null || isString(value) || isBoolean(value)) return null;
  if (isNumber(value))
    return Number.isFinite(value) ? null : `${where} is ${String(value)}`;
  if (value === undefined) return `${where} is undefined`;
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const problem = jsonProblem(item, `${at}[${index}]`);
      if (problem) return problem;
    }
    return null;
  }
  if (isFunction(value)) return `${where} is a function`;
  if (!isObjectLike(value))
    return `${where} is neither text, a number, a list nor an object`;
  const prototype: { constructor?: { name?: string } } | null =
    Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null)
    return `${where} is a ${prototype.constructor?.name || 'class instance'}`;
  for (const [key, field] of Object.entries(value)) {
    const problem = jsonProblem(field, at ? `${at}.${key}` : key);
    if (problem) return problem;
  }
  return null;
}

/** A JSON object: not text, a number, a boolean, null or a list. */
export function isJsonObject(value: JsonValue): value is JsonObject {
  return isObjectLike(value) && !isList(value);
}

export type JsonParseResult =
  | { readonly ok: true; readonly value: JsonValue }
  | { readonly ok: false; readonly reason: string };

/** Parses text into a `JsonValue`; the failure branch names the problem instead of throwing. */
export function parseJson(text: string): JsonParseResult {
  let parsed: JsonValue;
  try {
    const candidate: unknown = JSON.parse(text);
    if (!isJsonValue(candidate))
      return { ok: false, reason: 'not a JSON value' };
    parsed = candidate;
  } catch (cause) {
    return {
      ok: false,
      reason: cause instanceof Error ? cause.message : 'invalid JSON',
    };
  }
  return { ok: true, value: parsed };
}
